/**
 * Organization Setup Page
 * Company Profile (standalone) · Organization Structure (branches, departments, designations)
 * Reuses existing API: /setup/departments|designations|locations (GET/POST/PATCH/DELETE)
 * Company profile: /organisation/me/profile-status + /setup/org-profile
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Building2,
  Layers,
  Loader2,
  MapPin,
  Pencil,
  Plus,
  Power,
  Trash2,
  Users,
  X,
} from "lucide-react";
import API from "../../services/api";
import { fetchOrgProfileStatus, saveOrgProfileMinimal } from "../../services/orgProfileService";
import { ToastStack, useToastStack } from "../ui/ToastStack";
import ConfirmDialog from "../ui/ConfirmDialog";
import OrganizationStructureHierarchy from "./OrganizationStructureHierarchy";
import { useFoundationSetup } from "../../Moduels/Context/FoundationSetupContext";
import { useSetupProgress } from "../../Moduels/Context/SetupProgressContext";
import { navigateToSettingsHome } from "./settingsExit";
import "../../styles/organizationSetup.css";

/* ─── constants ────────────────────────────────────────────────────────────── */

const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa",
  "Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala",
  "Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland",
  "Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura",
  "Uttar Pradesh","Uttarakhand","West Bengal","Delhi","Jammu and Kashmir",
  "Ladakh","Puducherry",
];

/* ─── helpers ──────────────────────────────────────────────────────────────── */

function formatErr(err, fallback = "Something went wrong") {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || fallback;
  return typeof d === "string" ? d : JSON.stringify(d);
}

function autoCode(prefix, index) {
  return `${prefix}${String(index + 1).padStart(3, "0")}`;
}

/* ─── Section header ───────────────────────────────────────────────────────── */
function SectionHeader({ icon: Icon, iconTone = "blue", title, subtitle, action }) {
  return (
    <div className="os-sec-header">
      <div className={`os-sec-icon-wrap os-icon-tone--${iconTone}`}>
        <Icon size={18} />
      </div>
      <div className="os-sec-title-group">
        <h2 className="os-sec-title">{title}</h2>
        <p className="os-sec-subtitle">{subtitle}</p>
      </div>
      {action}
    </div>
  );
}

/* ─── Status badge ─────────────────────────────────────────────────────────── */
function StatusBadge({ active }) {
  return (
    <span className={`os-badge ${active ? "os-badge--active" : "os-badge--inactive"}`}>
      {active ? "Active" : "Inactive"}
    </span>
  );
}

/* ─── Action buttons ───────────────────────────────────────────────────────── */
function ActionBtn({ variant, onClick, title, disabled }) {
  return (
    <button
      type="button"
      className={`os-action-btn os-action-btn--${variant}`}
      onClick={onClick}
      title={title}
      disabled={disabled}
    >
      {variant === "edit" && <Pencil size={14} />}
      {variant === "delete" && <Trash2 size={14} />}
      {variant === "toggle" && <Power size={14} />}
    </button>
  );
}

/* ─── Inline Add/Edit row modal ─────────────────────────────────────────────── */
function RecordDrawer({ open, title, fields, values, onChange, onSave, onClose, saving, errors }) {
  if (!open) return null;
  return (
    <div className="os-drawer-overlay" onClick={onClose}>
      <div className="os-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="os-drawer-header">
          <h3>{title}</h3>
          <button className="os-drawer-close" onClick={onClose} disabled={saving}>
            <X size={18} />
          </button>
        </div>
        <div className="os-drawer-body">
          {fields.map((f) => (
            <div key={f.key} className="os-drawer-field">
              <label className="os-drawer-label">
                {f.label}
                {f.required && <span className="os-req"> *</span>}
              </label>
              {f.type === "select" ? (
                <select
                  className={`os-drawer-input${errors?.[f.key] ? " os-input-err" : ""}`}
                  value={values[f.key] || ""}
                  onChange={(e) => onChange(f.key, e.target.value)}
                >
                  <option value="">Select {f.label}</option>
                  {f.options?.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              ) : f.type === "textarea" ? (
                <textarea
                  className={`os-drawer-input os-drawer-textarea${errors?.[f.key] ? " os-input-err" : ""}`}
                  value={values[f.key] || ""}
                  onChange={(e) => onChange(f.key, e.target.value)}
                  placeholder={f.placeholder}
                />
              ) : (
                <input
                  type={f.type || "text"}
                  className={`os-drawer-input${errors?.[f.key] ? " os-input-err" : ""}`}
                  value={values[f.key] || ""}
                  onChange={(e) => onChange(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  maxLength={f.maxLength}
                  style={f.uppercase ? { textTransform: "uppercase" } : undefined}
                />
              )}
              {errors?.[f.key] && <p className="os-input-err-msg">{errors[f.key]}</p>}
            </div>
          ))}
        </div>
        <div className="os-drawer-footer">
          <button className="os-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="os-btn-primary" onClick={onSave} disabled={saving}>
            {saving ? <Loader2 size={14} className="os-spin" /> : null}
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Company Profile Card ─────────────────────────────────────────────────── */
function CompanyProfileCard({ onEdit }) {
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrgProfileStatus()
      .then((d) => setOrg(d?.org || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="os-card os-card--profile">
        <SectionHeader icon={Building2} iconTone="blue" title="1.1 Company Profile" subtitle="Store company master information." />
        <div className="os-card-body os-shimmer-rows">
          {[1,2,3].map(i => <div key={i} className="os-shimmer-row" />)}
        </div>
      </div>
    );
  }

  const fields = [
    { label: "Organization Name", value: org?.name },
    { label: "GSTIN", value: org?.gst_number },
    { label: "Legal Entity Name", value: org?.legal_name },
    { label: "State", value: org?.state },
    { label: "PAN", value: org?.pan },
    { label: "Pincode", value: org?.postal_code },
    { label: "TAN (in TDS Settings)", value: org?.tan_number },
    { label: "Country", value: org?.country || "India" },
  ];

  return (
    <div className="os-card os-card--profile">
      <SectionHeader
        icon={Building2}
        iconTone="blue"
        title="1.1 Company Profile"
        subtitle="Store company master information."
        action={
          <button className="os-edit-btn" onClick={onEdit}>
            <Pencil size={14} />
            Edit
          </button>
        }
      />
      <div className="os-card-body">
        <div className="os-profile-grid">
          {fields.map((f) => (
            <div key={f.label} className="os-profile-item">
              <span className="os-profile-label">{f.label}</span>
              <span className="os-profile-value">{f.value || <span className="os-empty-val">—</span>}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Company Profile Edit Modal ───────────────────────────────────────────── */
function CompanyProfileModal({ open, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", legal_name: "", pan: "", tan_number: "", state: "", address: "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [apiErr, setApiErr] = useState("");

  useEffect(() => {
    if (!open) return;
    fetchOrgProfileStatus().then((d) => {
      const o = d?.org || {};
      setForm({
        name: o.name || "",
        legal_name: o.legal_name || "",
        pan: o.pan || "",
        tan_number: o.tan_number || "",
        state: o.state || "",
        address: o.address || "",
      });
    }).catch(() => {});
  }, [open]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Organisation name is required";
    const pan = form.pan.trim().toUpperCase();
    if (pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan)) e.pan = "Invalid PAN (e.g. ABCDE1234F)";
    const tan = form.tan_number.trim().toUpperCase();
    if (tan && tan.length < 10) e.tan_number = "TAN must be at least 10 characters";
    if (!form.state.trim()) e.state = "State is required";
    if (!form.address.trim()) e.address = "Address is required";
    return e;
  };

  const onChange = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setErrors((e) => ({ ...e, [key]: undefined }));
    setApiErr("");
  };

  const onSave = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      await saveOrgProfileMinimal({
        name: form.name.trim(),
        legal_name: form.legal_name.trim() || undefined,
        pan: form.pan.trim().toUpperCase() || undefined,
        tan_number: form.tan_number.trim().toUpperCase() || undefined,
        state: form.state.trim(),
        address: form.address.trim(),
        country: "India",
      });
      onSaved?.();
      onClose();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setApiErr(typeof d === "string" ? d : "Could not save profile. Try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="os-modal-overlay" onClick={onClose}>
      <div className="os-modal" onClick={(e) => e.stopPropagation()}>
        <div className="os-modal-header">
          <div>
            <div className="os-modal-badge">
              <Building2 size={13} />
              <span>ORGANISATION PROFILE</span>
            </div>
            <h2 className="os-modal-title">Update company profile</h2>
          </div>
          <button className="os-drawer-close" onClick={onClose} disabled={saving}><X size={18} /></button>
        </div>

        <div className="os-modal-body">
          {apiErr && <div className="os-banner-err">{apiErr}</div>}

          <div className="os-drawer-field">
            <label className="os-drawer-label">Organisation Name <span className="os-req">*</span></label>
            <input
              className={`os-drawer-input${errors.name ? " os-input-err" : ""}`}
              value={form.name}
              onChange={(e) => onChange("name", e.target.value)}
              placeholder="Sample Payroll Systems Pvt Ltd"
            />
            {errors.name && <p className="os-input-err-msg">{errors.name}</p>}
          </div>

          <div className="os-drawer-field">
            <label className="os-drawer-label">Legal Name <span className="os-label-opt">(optional)</span></label>
            <input
              className="os-drawer-input"
              value={form.legal_name}
              onChange={(e) => onChange("legal_name", e.target.value)}
              placeholder="Same as registered legal entity"
            />
          </div>

          <div className="os-field-row">
            <div className="os-drawer-field">
              <label className="os-drawer-label">PAN <span className="os-label-opt">(optional)</span></label>
              <input
                className={`os-drawer-input${errors.pan ? " os-input-err" : ""}`}
                value={form.pan}
                onChange={(e) => onChange("pan", e.target.value.toUpperCase())}
                placeholder="ABCDE1234L"
                maxLength={10}
                style={{ textTransform: "uppercase" }}
              />
              {errors.pan && <p className="os-input-err-msg">{errors.pan}</p>}
            </div>
            <div className="os-drawer-field">
              <label className="os-drawer-label">TAN <span className="os-label-opt">(optional)</span></label>
              <input
                className={`os-drawer-input${errors.tan_number ? " os-input-err" : ""}`}
                value={form.tan_number}
                onChange={(e) => onChange("tan_number", e.target.value.toUpperCase())}
                placeholder="HYD1234HAL"
                style={{ textTransform: "uppercase" }}
              />
              {errors.tan_number && <p className="os-input-err-msg">{errors.tan_number}</p>}
            </div>
          </div>

          <div className="os-drawer-field">
            <label className="os-drawer-label">State <span className="os-req">*</span></label>
            <select
              className={`os-drawer-input${errors.state ? " os-input-err" : ""}`}
              value={form.state}
              onChange={(e) => onChange("state", e.target.value)}
            >
              <option value="">Select state</option>
              {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            {errors.state && <p className="os-input-err-msg">{errors.state}</p>}
          </div>

          <div className="os-drawer-field">
            <label className="os-drawer-label">Address <span className="os-req">*</span></label>
            <textarea
              className={`os-drawer-input os-drawer-textarea${errors.address ? " os-input-err" : ""}`}
              value={form.address}
              onChange={(e) => onChange("address", e.target.value)}
              placeholder="Registered office address"
            />
            {errors.address && <p className="os-input-err-msg">{errors.address}</p>}
          </div>
        </div>

        <div className="os-modal-footer">
          <button className="os-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="os-btn-primary os-btn-full" onClick={onSave} disabled={saving}>
            {saving ? <Loader2 size={14} className="os-spin" /> : null}
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Branches / Locations Table ───────────────────────────────────────────── */
function activeStructureRows(list) {
  if (!Array.isArray(list)) return [];
  return list.filter((r) => r.is_active !== false);
}

function BranchesSection({ addToast, onCountChange, onStructureChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await API.get("/setup/locations");
      setRows(data || []);
    } catch (e) { addToast(formatErr(e, "Failed to load branches"), "error"); }
    finally { setLoading(false); }
  }, [addToast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!loading) onCountChange?.(rows.length);
  }, [loading, rows.length, onCountChange]);

  const openCreate = () => {
    setEditing(null);
    setValues({ name: "", state: "", city: "" });
    setErrors({});
    setDrawerOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setValues({ name: row.name, state: row.state || "", city: row.city || "" });
    setErrors({});
    setDrawerOpen(true);
  };

  const validate = () => {
    const e = {};
    if (!values.name?.trim()) e.name = "Branch name is required";
    return e;
  };

  const onSave = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      if (editing) {
        const { data } = await API.patch(`/setup/locations/${editing.id}`, values);
        setRows((r) => r.map((x) => (x.id === editing.id ? data : x)));
        addToast("Branch updated", "success");
      } else {
        const { data } = await API.post("/setup/locations", values);
        setRows((r) => [...r, data]);
        addToast("Branch added", "success");
      }
      setDrawerOpen(false);
      onStructureChange?.();
    } catch (err) { addToast(formatErr(err, "Failed to save branch"), "error"); }
    finally { setSaving(false); }
  };

  const toggleActive = async (row) => {
    try {
      const { data } = await API.patch(`/setup/locations/${row.id}`, { is_active: !row.is_active });
      setRows((r) => r.map((x) => (x.id === row.id ? data : x)));
      onStructureChange?.();
    } catch (err) { addToast(formatErr(err, "Failed to update status"), "error"); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await API.delete(`/setup/locations/${deleteTarget.id}`);
      setRows((r) => r.filter((x) => x.id !== deleteTarget.id));
      addToast(`"${deleteTarget.name}" deleted`, "success");
      setDeleteTarget(null);
      onStructureChange?.();
    } catch (err) { addToast(formatErr(err, "Failed to delete"), "error"); }
    finally { setSaving(false); }
  };

  return (
    <div className="os-card os-card--branches">
      <SectionHeader
        icon={MapPin}
        iconTone="emerald"
        title="Branches / Locations"
        subtitle="Office branches, plants, and work sites used across payroll and attendance."
        action={
          <button className="os-add-btn" onClick={openCreate}>
            <Plus size={14} />
            Add Branch
          </button>
        }
      />
      {loading ? (
        <div className="os-table-loading"><Loader2 size={18} className="os-spin" /></div>
      ) : (
        <div className="os-table-wrap">
          <table className="os-table">
            <thead>
              <tr>
                <th>Branch Code</th>
                <th>Branch Name</th>
                <th>State</th>
                <th>Status</th>
                <th className="os-th-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={5} className="os-empty-row">No branches yet. Click "+ Add Branch" to get started.</td></tr>
              )}
              {rows.map((row, i) => (
                <tr key={row.id}>
                  <td className="os-td-code">{autoCode("BR", i)}</td>
                  <td className="os-td-name">{row.name}</td>
                  <td>{row.state || <span className="os-empty-val">—</span>}</td>
                  <td><StatusBadge active={row.is_active !== false} /></td>
                  <td className="os-td-actions">
                    <ActionBtn variant="edit" onClick={() => openEdit(row)} title="Edit" />
                    <ActionBtn variant="delete" onClick={() => setDeleteTarget(row)} title="Delete" />
                    <ActionBtn variant="toggle" onClick={() => toggleActive(row)} title="Toggle active" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RecordDrawer
        open={drawerOpen}
        title={editing ? "Edit Branch" : "Add Branch"}
        fields={[
          { key: "name", label: "Branch Name", required: true, placeholder: "e.g. Head Office - Mumbai" },
          { key: "state", label: "State", type: "select", options: INDIAN_STATES },
          { key: "city", label: "City", placeholder: "e.g. Mumbai" },
        ]}
        values={values}
        onChange={(k, v) => { setValues((p) => ({ ...p, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); }}
        onSave={onSave}
        onClose={() => !saving && setDrawerOpen(false)}
        saving={saving}
        errors={errors}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete branch?"
        message={deleteTarget ? `Delete "${deleteTarget.name}"? This cannot be undone.` : ""}
        confirmLabel="Delete"
        danger
        onConfirm={confirmDelete}
        onCancel={() => !saving && setDeleteTarget(null)}
        loading={saving}
      />
    </div>
  );
}

/* ─── Departments Table ────────────────────────────────────────────────────── */
function DepartmentsSection({ addToast, onCountChange, onStructureChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await API.get("/setup/departments");
      setRows(data || []);
    } catch (e) { addToast(formatErr(e, "Failed to load departments"), "error"); }
    finally { setLoading(false); }
  }, [addToast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!loading) onCountChange?.(rows.length);
  }, [loading, rows.length, onCountChange]);

  const openCreate = () => { setEditing(null); setValues({ name: "", description: "" }); setErrors({}); setDrawerOpen(true); };
  const openEdit = (row) => { setEditing(row); setValues({ name: row.name, description: row.description || "" }); setErrors({}); setDrawerOpen(true); };

  const validate = () => {
    const e = {};
    if (!values.name?.trim()) e.name = "Department name is required";
    return e;
  };

  const onSave = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      if (editing) {
        const { data } = await API.patch(`/setup/departments/${editing.id}`, values);
        setRows((r) => r.map((x) => (x.id === editing.id ? data : x)));
        addToast("Department updated", "success");
      } else {
        const { data } = await API.post("/setup/departments", { name: values.name });
        setRows((r) => [...r, data]);
        addToast("Department added", "success");
      }
      setDrawerOpen(false);
      onStructureChange?.();
    } catch (err) { addToast(formatErr(err, "Failed to save department"), "error"); }
    finally { setSaving(false); }
  };

  const toggleActive = async (row) => {
    try {
      const { data } = await API.patch(`/setup/departments/${row.id}`, { is_active: !row.is_active });
      setRows((r) => r.map((x) => (x.id === row.id ? data : x)));
      onStructureChange?.();
    } catch (err) { addToast(formatErr(err, "Failed to update status"), "error"); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await API.delete(`/setup/departments/${deleteTarget.id}`);
      setRows((r) => r.filter((x) => x.id !== deleteTarget.id));
      addToast(`"${deleteTarget.name}" deleted`, "success");
      setDeleteTarget(null);
      onStructureChange?.();
    } catch (err) { addToast(formatErr(err, "Failed to delete"), "error"); }
    finally { setSaving(false); }
  };

  return (
    <div className="os-card os-card--departments">
      <SectionHeader
        icon={Layers}
        iconTone="violet"
        title="Departments"
        subtitle="Group employees into teams and cost centres."
        action={
          <button className="os-add-btn" onClick={openCreate}>
            <Plus size={14} />
            Add Department
          </button>
        }
      />
      {loading ? (
        <div className="os-table-loading"><Loader2 size={18} className="os-spin" /></div>
      ) : (
        <div className="os-table-wrap">
          <table className="os-table">
            <thead>
              <tr>
                <th>Department Code</th>
                <th>Department Name</th>
                <th>Status</th>
                <th className="os-th-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={4} className="os-empty-row">No departments yet.</td></tr>
              )}
              {rows.map((row, i) => (
                <tr key={row.id}>
                  <td className="os-td-code">{row.code || autoCode("DPT", i)}</td>
                  <td className="os-td-name">{row.name}</td>
                  <td><StatusBadge active={row.is_active !== false} /></td>
                  <td className="os-td-actions">
                    <ActionBtn variant="edit" onClick={() => openEdit(row)} title="Edit" />
                    <ActionBtn variant="delete" onClick={() => setDeleteTarget(row)} title="Delete" />
                    <ActionBtn variant="toggle" onClick={() => toggleActive(row)} title="Toggle active" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RecordDrawer
        open={drawerOpen}
        title={editing ? "Edit Department" : "Add Department"}
        fields={[
          { key: "name", label: "Department Name", required: true, placeholder: "e.g. Human Resources" },
          { key: "description", label: "Description", type: "textarea", placeholder: "Optional description" },
        ]}
        values={values}
        onChange={(k, v) => { setValues((p) => ({ ...p, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); }}
        onSave={onSave}
        onClose={() => !saving && setDrawerOpen(false)}
        saving={saving}
        errors={errors}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete department?"
        message={deleteTarget ? `Delete "${deleteTarget.name}"? This cannot be undone.` : ""}
        confirmLabel="Delete"
        danger
        onConfirm={confirmDelete}
        onCancel={() => !saving && setDeleteTarget(null)}
        loading={saving}
      />
    </div>
  );
}

/* ─── Designations Table ───────────────────────────────────────────────────── */
function DesignationsSection({ addToast, onCountChange, onStructureChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await API.get("/setup/designations");
      setRows(data || []);
    } catch (e) { addToast(formatErr(e, "Failed to load designations"), "error"); }
    finally { setLoading(false); }
  }, [addToast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!loading) onCountChange?.(rows.length);
  }, [loading, rows.length, onCountChange]);

  const openCreate = () => { setEditing(null); setValues({ name: "", description: "" }); setErrors({}); setDrawerOpen(true); };
  const openEdit = (row) => { setEditing(row); setValues({ name: row.name, description: row.description || "" }); setErrors({}); setDrawerOpen(true); };

  const validate = () => {
    const e = {};
    if (!values.name?.trim()) e.name = "Designation name is required";
    return e;
  };

  const onSave = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      if (editing) {
        const { data } = await API.patch(`/setup/designations/${editing.id}`, values);
        setRows((r) => r.map((x) => (x.id === editing.id ? data : x)));
        addToast("Designation updated", "success");
      } else {
        const { data } = await API.post("/setup/designations", { name: values.name });
        setRows((r) => [...r, data]);
        addToast("Designation added", "success");
      }
      setDrawerOpen(false);
      onStructureChange?.();
    } catch (err) { addToast(formatErr(err, "Failed to save designation"), "error"); }
    finally { setSaving(false); }
  };

  const toggleActive = async (row) => {
    try {
      const { data } = await API.patch(`/setup/designations/${row.id}`, { is_active: !row.is_active });
      setRows((r) => r.map((x) => (x.id === row.id ? data : x)));
      onStructureChange?.();
    } catch (err) { addToast(formatErr(err, "Failed to update status"), "error"); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await API.delete(`/setup/designations/${deleteTarget.id}`);
      setRows((r) => r.filter((x) => x.id !== deleteTarget.id));
      addToast(`"${deleteTarget.name}" deleted`, "success");
      setDeleteTarget(null);
      onStructureChange?.();
    } catch (err) { addToast(formatErr(err, "Failed to delete"), "error"); }
    finally { setSaving(false); }
  };

  return (
    <div className="os-card os-card--designations">
      <SectionHeader
        icon={Users}
        iconTone="amber"
        title="Designations"
        subtitle="Define job titles and role hierarchy for employees."
        action={
          <button className="os-add-btn" onClick={openCreate}>
            <Plus size={14} />
            Add Designation
          </button>
        }
      />
      {loading ? (
        <div className="os-table-loading"><Loader2 size={18} className="os-spin" /></div>
      ) : (
        <div className="os-table-wrap">
          <table className="os-table">
            <thead>
              <tr>
                <th>Designation Code</th>
                <th>Designation Name</th>
                <th>Status</th>
                <th className="os-th-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={4} className="os-empty-row">No designations yet.</td></tr>
              )}
              {rows.map((row, i) => (
                <tr key={row.id}>
                  <td className="os-td-code">{autoCode("DESG", i)}</td>
                  <td className="os-td-name">{row.name}</td>
                  <td><StatusBadge active={row.is_active !== false} /></td>
                  <td className="os-td-actions">
                    <ActionBtn variant="edit" onClick={() => openEdit(row)} title="Edit" />
                    <ActionBtn variant="delete" onClick={() => setDeleteTarget(row)} title="Delete" />
                    <ActionBtn variant="toggle" onClick={() => toggleActive(row)} title="Toggle active" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RecordDrawer
        open={drawerOpen}
        title={editing ? "Edit Designation" : "Add Designation"}
        fields={[
          { key: "name", label: "Designation Name", required: true, placeholder: "e.g. Chief Executive Officer" },
          { key: "description", label: "Description", type: "textarea", placeholder: "Optional description" },
        ]}
        values={values}
        onChange={(k, v) => { setValues((p) => ({ ...p, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); }}
        onSave={onSave}
        onClose={() => !saving && setDrawerOpen(false)}
        saving={saving}
        errors={errors}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete designation?"
        message={deleteTarget ? `Delete "${deleteTarget.name}"? This cannot be undone.` : ""}
        confirmLabel="Delete"
        danger
        onConfirm={confirmDelete}
        onCancel={() => !saving && setDeleteTarget(null)}
        loading={saving}
      />
    </div>
  );
}

/* ─── Organization Structure (branches + departments + designations) ───────── */

const STRUCTURE_TABS = [
  {
    id: "branches",
    label: "Branches",
    hint: "Office locations & work sites",
    noun: "location",
    nouns: "locations",
    icon: MapPin,
    tone: "emerald",
  },
  {
    id: "departments",
    label: "Departments",
    hint: "Teams & cost centres",
    noun: "department",
    nouns: "departments",
    icon: Layers,
    tone: "violet",
  },
  {
    id: "designations",
    label: "Designations",
    hint: "Job titles & roles",
    noun: "designation",
    nouns: "designations",
    icon: Users,
    tone: "amber",
  },
];

function structureCountNoun(tab, count) {
  if (count == null) return "Loading…";
  const n = Number(count) || 0;
  return n === 1 ? tab.noun : tab.nouns;
}

function OrganizationStructureView({ addToast, initialTab }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab") || initialTab;
  const activeTab = STRUCTURE_TABS.some((t) => t.id === tabFromUrl) ? tabFromUrl : "branches";
  const foundationCtx = useFoundationSetup();
  const setupProgressCtx = useSetupProgress();
  const [structureRefreshToken, setStructureRefreshToken] = useState(0);
  const [counts, setCounts] = useState({
    branches: null,
    departments: null,
    designations: null,
  });

  const syncStructureCounts = useCallback(async () => {
    try {
      const [locRes, deptRes, desRes] = await Promise.all([
        API.get("/setup/locations"),
        API.get("/setup/departments"),
        API.get("/setup/designations"),
      ]);
      setCounts({
        branches: activeStructureRows(locRes.data).length,
        departments: activeStructureRows(deptRes.data).length,
        designations: activeStructureRows(desRes.data).length,
      });
    } catch {
      /* keep last known counts */
    }
  }, []);

  const onStructureChange = useCallback(() => {
    setStructureRefreshToken((t) => t + 1);
    syncStructureCounts();
    foundationCtx?.refetch?.();
    setupProgressCtx?.refetch?.();
  }, [foundationCtx, setupProgressCtx, syncStructureCounts]);

  useEffect(() => {
    syncStructureCounts();
  }, [syncStructureCounts, structureRefreshToken]);

  const onBranchesCount = useCallback((n) => {
    setCounts((c) => (c.branches === n ? c : { ...c, branches: n }));
  }, []);
  const onDepartmentsCount = useCallback((n) => {
    setCounts((c) => (c.departments === n ? c : { ...c, departments: n }));
  }, []);
  const onDesignationsCount = useCallback((n) => {
    setCounts((c) => (c.designations === n ? c : { ...c, designations: n }));
  }, []);

  const setTab = (id) => {
    if (id === activeTab) return;
    setSearchParams({ tab: id }, { replace: true });
  };

  useEffect(() => {
    if (searchParams.get("tab")) return;
    const next = STRUCTURE_TABS.some((t) => t.id === initialTab) ? initialTab : "branches";
    setSearchParams({ tab: next }, { replace: true });
  }, [initialTab, searchParams, setSearchParams]);

  return (
    <div className="os-structure">
      <OrganizationStructureHierarchy refreshToken={structureRefreshToken} />

      <div className="os-structure-tabs" role="tablist" aria-label="Organization structure sections">
        {STRUCTURE_TABS.map((tab) => {
          const Icon = tab.icon;
          const selected = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              className={`os-structure-tab os-structure-tab--${tab.tone}${selected ? " os-structure-tab--active" : ""}`}
              onClick={() => setTab(tab.id)}
            >
              <div className={`os-structure-tab-icon os-icon-tone--${tab.tone}`}>
                <Icon size={18} />
              </div>
              <div className="os-structure-tab-copy">
                <span className="os-structure-tab-label-row">
                  <span className="os-structure-tab-label">{tab.label}</span>
                  {selected ? <span className="os-structure-tab-pill">Active</span> : null}
                </span>
                <span className="os-structure-tab-hint">{tab.hint}</span>
              </div>
              <div className="os-structure-tab-stat">
                <span className="os-structure-tab-count">
                  {counts[tab.id] == null ? "—" : counts[tab.id]}
                </span>
                <span className="os-structure-tab-noun">{structureCountNoun(tab, counts[tab.id])}</span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="os-structure-stage">
        <div hidden={activeTab !== "branches"} className="os-structure-panel">
          <BranchesSection addToast={addToast} onCountChange={onBranchesCount} onStructureChange={onStructureChange} />
        </div>
        <div hidden={activeTab !== "departments"} className="os-structure-panel">
          <DepartmentsSection addToast={addToast} onCountChange={onDepartmentsCount} onStructureChange={onStructureChange} />
        </div>
        <div hidden={activeTab !== "designations"} className="os-structure-panel">
          <DesignationsSection addToast={addToast} onCountChange={onDesignationsCount} onStructureChange={onStructureChange} />
        </div>
      </div>
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────────────────────────────── */
const SECTION_META = {
  "company-profile": {
    title: "Company Profile",
    sub: "Store and manage company master information.",
  },
  structure: {
    title: "Organization Structure",
    sub: "See your organisation map, then manage branches, departments, and designations in one place.",
  },
};

export { BranchesSection, DepartmentsSection, DesignationsSection };

export default function OrganizationSetupPage({ section = "company-profile" }) {
  const navigate = useNavigate();
  const { toasts, addToast, removeToast } = useToastStack();
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileKey, setProfileKey] = useState(0);

  const resolvedSection =
    section === "structure" || ["branches", "departments", "designations"].includes(section)
      ? "structure"
      : section;
  const initialTab = ["branches", "departments", "designations"].includes(section)
    ? section
    : undefined;

  const meta = SECTION_META[resolvedSection] || SECTION_META["company-profile"];
  return (
    <div className={`os-page${resolvedSection === "structure" ? " os-page--structure" : ""}`}>
      <ToastStack toasts={toasts} onDismiss={removeToast} />

      <div className="os-page-header">
        <h1 className="os-page-title">{meta.title}</h1>
        <p className="os-page-sub">{meta.sub}</p>
      </div>

      {resolvedSection === "company-profile" && (
        <CompanyProfileCard
          key={profileKey}
          onEdit={() => setProfileModalOpen(true)}
          standalone
        />
      )}

      {resolvedSection === "structure" && (
        <OrganizationStructureView addToast={addToast} initialTab={initialTab} />
      )}

      <CompanyProfileModal
        open={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        onSaved={() => {
          setProfileKey((k) => k + 1);
          navigateToSettingsHome(navigate, { replace: true });
        }}
      />
    </div>
  );
}
